import React, { useEffect, useRef, useState } from "react";
import { getYouTubeAuthUrl } from "../../features/google/platforms/youtube/services/youtubeAuthentication";
import { useLocation, useNavigate } from "react-router-dom";
import { connectToGoogleMyBusiness } from "../../features/google/platforms/gmb/services/gmbAuthentication";
import YouTubeLogo from "../../assests/YouTube_Logo/Digital/01 Full Color/yt_logo_fullcolor_almostblack_digital.png";
import { useAuth } from "../../context/AuthContext";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import { debug } from "../../utils/debugLogger";
import {
  getMetaClientId,
  getFacebookRedirectUri,
  requireMetaAppCredentials,
} from "../../features/meta/config/metaEnv";
import { getLinkedInAuthUrl, getLinkedInBusinessAuthUrl } from "../../features/linkedin/linkedinAuthentication";
import { initiateWordPressOAuth } from "../../features/wordpress/platforms/wordpress/services/wordpressAuthentication";
import {
  disconnectGHLLocation,
  initiateGHLOAuth,
} from "../../features/ghl/services/ghlAuthentication";
import { createOAuthState } from "../../utils/oauthState";
import { fetchCustomerById } from "../../services/customerService";
import { ConnectedProfile, ConnectedProfileDetail } from "../../types/customer";
import { deleteSocialToken } from "../../services/socialTokenService";
import { Platform } from "../../types/Platforms";
// Imported from the individual Drive modules, not the features/media barrel: the
// barrel also exports FileUploader, which pulls in contentService -> cognitoService
// and builds a CognitoUserPool at module scope. This page needs none of that.
import { isGoogleDriveAttachmentsConfigured } from "../../features/media/providers/googleDrive/googleDriveEnv";
import { useGoogleDriveConnectionStatus } from "../../features/media/providers/googleDrive/useGoogleDriveConnectionStatus";
import { startGoogleDriveOAuthFlow } from "../../features/media/providers/googleDrive/googleDriveOAuth";
import { isGscConfigured } from "../../features/google/platforms/gsc/config/gscEnv";
import { getGscAuthUrl } from "../../features/google/platforms/gsc/services/gscAuthentication";
import { rememberGscOAuthCustomerId } from "../../features/google/platforms/gsc/services/gscSession";
import { bingProvider } from "../../features/bing/bingProvider";
import BingKeyDialog from "../../features/bing/components/BingKeyDialog";
import { rememberOAuthCustomerId } from "../../features/site-connections/siteConnectionSession";
import { useOptionalConnectionHealthContext } from "../../context/connectionHealthValue";
import { isTikTokConfigured } from "../../features/tiktok/config/tiktokEnv";
import {
  TIKTOK_OAUTH_STATE_PROVIDER,
  disconnectTikTok,
  getTikTokAuthUrl,
} from "../../features/tiktok/services/tiktokAuthentication";
import TikTokCardStatus from "../../features/tiktok/components/TikTokCardStatus";
import { WebsiteBlogSection } from "../../features/wordpress/platforms/wordpress/components/WebsiteBlogSection";
import { disconnectShopify, getHostedBlog, getShopifyConnection } from "../../features/wordpress/platforms/wordpress/services/blogPublishingService";
import { ShopifyBlogSection } from "../../features/shopify/components/ShopifyBlogSection";

// Deliberately not all 5 of social-management's MIGRATED_PLATFORMS yet, though
// the backend route (DELETE /{customer_id}/tokens) already supports every one
// of them identically.
//
// Disconnect deletes a real credential and the only way back is a full re-OAuth,
// so it is exposed one platform at a time as each is actually exercised.
// LinkedIn's disconnect -> reconnect -> publish round trip is verified against
// dev (2026-08-12, re-confirmed 2026-09-28). Facebook/Instagram/GMB/YouTube are
// enabled here on the strength of sharing that same shared deleteSocialToken /
// DELETE {customer_id}/tokens backend code path (no platform-specific branching
// except GMB's account_id, which handleDisconnect already threads through via
// detail.metadata?.account_id) — their OWN round trips through Meta's/Google's
// OAuth reconnect flow have NOT been separately run yet. Do that before relying
// on this for a given platform in prod. Widen this set per platform as each is
// confirmed — no backend change is needed, the route already handles them.
//
// WordPress keeps its own separate disconnect flow (ConnectedWordPressSites), so it
// is not listed here. GoHighLevel is not listed either, but for a different reason:
// it DOES get a Disconnect control, supplying its own transport through the
// `onDisconnect` field on its card. A GHL connection is a sub-account binding rather
// than a social token, so releasing it must go to DELETE /{customer_id}/ghl/disconnect,
// which revokes the GHLLocations row as well as deleting the credential.
const DISCONNECTABLE_PLATFORM_KEYS = new Set([
  "LINKEDIN", "LINKEDIN_BUSINESS", "FACEBOOK", "INSTAGRAM", "GMB", "YOUTUBE",
  // Search Console reconnects through a plain re-OAuth with no publishing side effects,
  // and its disconnect -> reconnect round trip is covered by the backend suite.
  "GOOGLE_SEARCH_CONSOLE",
  // Bing Webmaster (SGL-134) is read-only the same way and shares the same backend
  // route, which deletes the only copy of the owner's API key; its disconnect is
  // covered by the social-management suite, not yet by a round trip against Bing.
  "BING_WEBMASTER",
]);

interface ConnectPageProps {
  /** When true, layout fits inside Manage Social Media (no full-viewport centering). */
  embedded?: boolean;
}

/**
 * One card on the Connect tab. Most fields are cosmetic. The *Override fields exist
 * for integrations whose connection state does NOT live in the customer's
 * connected_profiles — currently only Google Drive, which stores a Secrets Manager
 * secret and therefore has to be asked about directly.
 */
/** Sections, by what the agents do with a connection (not by vendor), so every
 *  future tool has an obvious home. Cards keep their own connect/disconnect code. */
const SECTIONS: { key: string; title: string; why: string }[] = [
  { key: "social", title: "Social media", why: "Where your agents publish your posts." },
  { key: "website", title: "Website & blog", why: "Where your agents publish your blog posts." },
  { key: "search", title: "Search & listings", why: "How customers find you. Your agents read rankings and reviews here." },
  { key: "crm", title: "Customers & CRM", why: "Contacts, calls and messages your agents work with." },
  { key: "files", title: "Files & media", why: "Files and photos your agents can use." },
  // A card nobody assigned shows here (visible, never silently mis-filed).
  { key: "other", title: "Other connections", why: "More places your agents can work." },
];
const SECTION_BY_KEY: Record<string, string> = {
  FACEBOOK: "social", INSTAGRAM: "social", LINKEDIN: "social", LINKEDIN_BUSINESS: "social", YOUTUBE: "social", TIKTOK: "social",
  WORDPRESS: "website", HOSTED_BLOG: "website", SHOPIFY: "website",
  GMB: "search", GOOGLE_SEARCH_CONSOLE: "search", BING_WEBMASTER: "search",
  GHL: "crm",
  GOOGLE_DRIVE: "files",
};

/** TikTok badge (inline: no third-party logo hosting). */
const TIKTOK_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#000"/>' +
      '<path d="M34.5 12.5v25.5a7.5 7.5 0 1 1-7.5-7.5" fill="none" stroke="#25f4ee" stroke-width="5" stroke-linecap="round"/>' +
      '<path d="M36 14v25.5a7.5 7.5 0 1 1-7.5-7.5M36 14c1 6 5 10 11 10.5" fill="none" stroke="#fff" stroke-width="5" stroke-linecap="round"/></svg>'
  );

/** A simple, recognisable badge for the site-builder card (no third-party logo hosting). */
const HOSTED_BLOG_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#2f4941"/>' +
      '<rect x="13" y="16" width="38" height="32" rx="4" fill="none" stroke="#fff" stroke-width="3.5"/>' +
      '<path d="M13 25h38" stroke="#fff" stroke-width="3.5"/><circle cx="19" cy="20.5" r="1.8" fill="#fff"/>' +
      '<circle cx="25" cy="20.5" r="1.8" fill="#fff"/><path d="M20 33h24M20 40h16" stroke="#8cad9a" stroke-width="3.5" stroke-linecap="round"/></svg>'
  );

/** Shopify badge: a shopping bag in Shopify green (no third-party logo hosting). */
const SHOPIFY_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#5e8e3e"/>' +
      '<path d="M17 24h30l-2.5 26h-25z" fill="#fff"/>' +
      '<path d="M25 27v-6a7 7 0 0 1 14 0v6" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round"/>' +
      '<path d="M27 40.5c1.2 1.2 3 1.9 5 1.9 2.4 0 4-1.1 4-2.8 0-3.9-8.6-2.4-8.6-6.8 0-1.8 1.7-3 4.1-3 1.6 0 3 .5 4 1.3" fill="none" stroke="#5e8e3e" stroke-width="2.6" stroke-linecap="round"/></svg>'
  );

type PlatformCard = {
  name: string;
  platformKey?: string;
  connectedPlatform?: string;
  connectedUrnPrefix?: string;
  connectedUrnExcludePrefix?: string;
  logo: string;
  description: string;
  buttonClass: string;
  onConnect: () => void | Promise<void>;
  iconClass: string;
  hideTitle?: boolean;
  logoClass?: string;
  isComingSoon?: boolean;
  /** When defined, replaces the connected_profiles lookup for this card. */
  connectedOverride?: boolean;
  /** When defined, replaces the page-level connections spinner for this card. */
  statusLoadingOverride?: boolean;
  /**
   * When defined, replaces the shared deleteSocialToken transport for this card
   * and opts it into the Disconnect control without listing it in
   * DISCONNECTABLE_PLATFORM_KEYS. Only for platforms whose credential is not a
   * plain social token — currently GoHighLevel, whose release must also revoke a
   * GHLLocations row. Must reject on failure: handleDisconnect treats a resolved
   * promise as proof the connection is gone and prunes the card optimistically.
   */
  onDisconnect?: (detail: ConnectedProfileDetail) => Promise<void>;
  /** Extra content between the description and the Connect button (e.g. TikTok's live account). */
  cardExtra?: React.ReactNode;
  /** When true the stored grant is dead; the button reads "Reconnect" instead of "CONNECTED". */
  needsReconnect?: boolean;
  /** Replaces "CONNECTED" on a connected card whose click does something specific
   *  (Bing: "Update key", which opens its key panel). */
  connectedAction?: { label: string; icon: string; title: string };
  /** Shown with the disconnect confirmation (Bing: the key can also be deleted in Bing). */
  disconnectNote?: string;
};

/**
 * Whether a card shows a Disconnect control. Two ways to qualify, because not
 * every connection is released the same way: a card either opts in by platform
 * key and uses the shared deleteSocialToken route, or brings its own transport
 * via onDisconnect (see the note above DISCONNECTABLE_PLATFORM_KEYS).
 */
const canDisconnect = (platform: PlatformCard): boolean =>
  Boolean(platform.onDisconnect) ||
  DISCONNECTABLE_PLATFORM_KEYS.has(platform.platformKey ?? "");

/** Small inline spinner used on the connect buttons while status loads / connecting. */
const ButtonSpinner: React.FC = () => (
  <svg
    className="mr-2 h-5 w-5 animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

const ConnectPage: React.FC<ConnectPageProps> = ({ embedded = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { idToken, isAuthenticated } = useAuth();
  const { selectedCustomerId } = useCustomerWorkspace();
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);
  const [connectedProfiles, setConnectedProfiles] = useState<ConnectedProfile[]>([]);
  const [loadingPlatform, setLoadingPlatform] = useState<string | null>(null);
  // Sagelyn-hosted blog (Squarespace, Wix, any builder): one per Brand.
  const [hostedConnected, setHostedConnected] = useState(false);
  const [hostedLoading, setHostedLoading] = useState(true);
  const [showHostedBlog, setShowHostedBlog] = useState(false);
  // Shopify store blog (SGL-431): a custom app, so the card shows only for
  // Brands the backend allows (`available`), or one already connected.
  const [shopifyShown, setShopifyShown] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [shopifyLoading, setShopifyLoading] = useState(true);
  const [showShopify, setShowShopify] = useState(false);
  // True while we fetch the selected profile's connections, so cards show a
  // loading state instead of flashing "Connect" before the real status loads.
  const [loadingConnections, setLoadingConnections] = useState(false);
  // Which customer connectedProfiles were loaded for. On a brand switch the
  // first render still holds the OLD brand's profiles with loadingConnections
  // false; TikTok's live lookup must not fire with another brand's profile id.
  const [profilesCustomerId, setProfilesCustomerId] = useState<string | null>(null);
  // platformKey pending a disconnect confirmation, and platformKey actively disconnecting.
  const [confirmingDisconnect, setConfirmingDisconnect] = useState<string | null>(null);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null);
  // The customer Bing's key dialog was opened for, or null when it is closed. Taken
  // from the workspace at click time; the dialog closes if the workspace changes.
  const [bingDialogCustomerId, setBingDialogCustomerId] = useState<string | null>(null);
  // Absent outside MainLayout; the bell's poll, refreshed after a Bing save.
  const connectionHealth = useOptionalConnectionHealthContext();

  // Drive's connection is a Secrets Manager secret, not a connected_profiles entry,
  // so this card can't use isPlatformConnected — it asks the backend directly.
  // See docs/superpowers/specs/2026-08-13-google-drive-connect-card-design.md
  const googleDriveEnabled = isGoogleDriveAttachmentsConfigured();
  // No OAuth client configured means the card could only ever produce an error, so it
  // is not rendered at all (same rule as the Drive card above).
  const gscEnabled = isGscConfigured();
  const bingEnabled = bingProvider.isConfigured();
  // Hidden unless a TikTok client key is set, so the feature ships dormant.
  const tiktokEnabled = isTikTokConfigured();
  const [tiktokNeedsReconnect, setTiktokNeedsReconnect] = useState(false);
  const {
    connected: googleDriveConnected,
    loading: googleDriveStatusLoading,
  } = useGoogleDriveConnectionStatus({
    customerId: selectedCustomerId,
    idToken,
  });

  useEffect(() => {
    if (!selectedCustomerId || !idToken) {
      setConnectedProfiles([]);
      setProfilesCustomerId(null);
      setLoadingConnections(false);
      return;
    }
    let active = true;
    setLoadingConnections(true);
    fetchCustomerById(selectedCustomerId, idToken)
      .then((customer) => {
        if (active) {
          setConnectedProfiles(Array.isArray(customer.connected_profiles) ? customer.connected_profiles : []);
          setProfilesCustomerId(selectedCustomerId);
        }
      })
      .catch(() => {
        if (active) {
          setConnectedProfiles([]);
          setProfilesCustomerId(selectedCustomerId);
        }
      })
      .finally(() => {
        if (active) setLoadingConnections(false);
      });
    return () => {
      active = false;
    };
  }, [selectedCustomerId, idToken]);

  useEffect(() => {
    if (bingDialogCustomerId && bingDialogCustomerId !== selectedCustomerId) {
      setBingDialogCustomerId(null);
    }
  }, [bingDialogCustomerId, selectedCustomerId]);

  useEffect(() => {
    if (location.state) {
      const { success, message, error } = location.state as any;
      if (success && message) {
        setMessage({ text: message, type: "success" });
      } else if (error) {
        setMessage({ text: error, type: "error" });
      }
    }
  }, [location]);

  // Handlers for each platform
  const handleGoogleMyBusinessConnect = async () => {
    try {
      await connectToGoogleMyBusiness();
    } catch (error) {
      setLoadingPlatform(null);
      setMessage({
        text: "Failed to connect to Google My Business",
        type: "error",
      });
    }
  };
  const handleYouTubeConnect = async () => {
    try {
      const authUrl = await getYouTubeAuthUrl(createOAuthState("youtube"));
      window.location.href = authUrl;
    } catch (error) {
      setLoadingPlatform(null);
      setMessage({ text: "Failed to connect to YouTube", type: "error" });
    }
  };
  const handleSearchConsoleConnect = () => {
    // The grant is bound to the customer that is selected right now; pin it so a
    // workspace change during the Google round trip can't redirect it elsewhere.
    if (!selectedCustomerId) {
      setMessage({
        text: "Select a business profile before connecting Google Search Console.",
        type: "error",
      });
      return;
    }
    try {
      rememberGscOAuthCustomerId(selectedCustomerId);
      window.location.href = getGscAuthUrl(createOAuthState("gsc"));
    } catch (error) {
      setLoadingPlatform(null);
      setMessage({ text: "Failed to connect to Google Search Console", type: "error" });
    }
  };
  /**
   * Bing Webmaster (SGL-134): opens the API-key dialog, which connects (no site yet)
   * or updates the key (a site is connected). No redirect, so nothing is pinned.
   */
  const handleBingConnect = () => {
    setLoadingPlatform(null);
    if (!selectedCustomerId || !idToken) {
      setMessage({
        text: `Select a business profile before connecting ${bingProvider.label}.`,
        type: "error",
      });
      return;
    }
    setMessage(null);
    setBingDialogCustomerId(selectedCustomerId);
  };
  /**
   * Show the sites the dialog saved as connected now, the way disconnect prunes
   * optimistically: connected_profiles is written by customer-management from the
   * TokensStored event, so reading the customer back at once can still miss them.
   * The bell's health endpoint reads SocialTokens itself, so it is re-read.
   */
  const handleBingDialogClose = (savedSiteUrls: string[]) => {
    setBingDialogCustomerId(null);
    if (!savedSiteUrls.length) return;
    const platform = bingProvider.platformKey;
    setConnectedProfiles((prev) => {
      const existing = prev.find((p) => (p.platform || "").toUpperCase() === platform);
      const known = new Set((existing?.details ?? []).map((d) => String(d.id)));
      const added = savedSiteUrls
        .filter((url) => !known.has(url))
        // social-management's own metadata for a Bing site; it sends no category,
        // which the shared type requires, so it is empty here.
        .map((url): ConnectedProfileDetail => ({
          id: url,
          metadata: { name: url, type: "bing_webmaster_site", category: "" },
        }));
      if (!added.length) return prev;
      return existing
        ? prev.map((p) => (p === existing ? { ...p, details: [...p.details, ...added] } : p))
        : [...prev, { platform, details: added }];
    });
    connectionHealth?.refetch();
  };
  /** TikTok (SGL-170): same-tab redirect; the public callback recovers the session from tiktok_pending_token. */
  const handleTikTokConnect = () => {
    if (!isAuthenticated || !idToken || !selectedCustomerId) {
      setLoadingPlatform(null);
      setMessage({
        text: "Select a business profile and log in before connecting TikTok.",
        type: "error",
      });
      return;
    }
    try {
      const authUrl = getTikTokAuthUrl(createOAuthState(TIKTOK_OAUTH_STATE_PROVIDER));
      rememberOAuthCustomerId(TIKTOK_OAUTH_STATE_PROVIDER, selectedCustomerId);
      localStorage.setItem("tiktok_pending_token", idToken);
      window.location.href = authUrl;
    } catch (error: any) {
      localStorage.removeItem("tiktok_pending_token");
      setLoadingPlatform(null);
      setMessage({ text: error?.message || "Failed to connect to TikTok", type: "error" });
    }
  };
  const handleFacebookConnect = () => {
    try {
      const { clientId } = requireMetaAppCredentials();
      const redirectUri = getFacebookRedirectUri();
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        state: createOAuthState("facebook"),
        scope:
          "pages_show_list,pages_manage_posts,public_profile,pages_read_engagement,pages_manage_metadata,pages_read_user_content,read_insights",
      });
      const facebookAuthUrl = `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`;
      window.location.href = facebookAuthUrl;
    } catch (error: any) {
      setLoadingPlatform(null);
      setMessage({
        text: error?.message || "Failed to start Facebook connect",
        type: "error",
      });
    }
  };
  const handleInstagramConnect = () => {
    const clientId = getMetaClientId();
    if (!clientId?.trim()) {
      setLoadingPlatform(null);
      setMessage({
        text: "REACT_APP_META_CLIENT_ID is not set. For local development, add it to .env.local and restart the dev server.",
        type: "error",
      });
      return;
    }
    const redirectUri =
      process.env.REACT_APP_INSTAGRAM_REDIRECT_URI ||
      `${window.location.origin}/connect/instagram/callback`;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      state: createOAuthState("instagram"),
      scope:
        "instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement,instagram_manage_insights,business_management",
    });
    const instagramAuthUrl = `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`;
    window.location.href = instagramAuthUrl;
  };

  const handleLinkedInConnect = () => {
    debug.log("🔵 LinkedIn Connect button clicked");

    // Ensure user is authenticated before starting OAuth flow
    if (!isAuthenticated || !idToken) {
      debug.error("❌ User not authenticated");
      setLoadingPlatform(null);
      setMessage({
        text: "Please log in before connecting LinkedIn.",
        type: "error",
      });
      return;
    }

    try {
      const linkedInAuthUrl = getLinkedInAuthUrl(createOAuthState("linkedin"));
      localStorage.setItem("linkedin_pending_token", idToken);
      window.location.href = linkedInAuthUrl;
    } catch (err: any) {
      setLoadingPlatform(null);
      setMessage({ text: err.message || "LinkedIn configuration error.", type: "error" });
    }
  };

  const handleLinkedInBusinessConnect = () => {
    debug.log("🔵 LinkedIn Business Page Connect button clicked");

    if (!isAuthenticated || !idToken) {
      setLoadingPlatform(null);
      setMessage({
        text: "Please log in before connecting a LinkedIn Business Page.",
        type: "error",
      });
      return;
    }

    try {
      const linkedInAuthUrl = getLinkedInBusinessAuthUrl(createOAuthState("linkedin_business"));
      localStorage.setItem("linkedin_pending_token", idToken);
      window.location.href = linkedInAuthUrl;
    } catch (err: any) {
      setLoadingPlatform(null);
      setMessage({ text: err.message || "LinkedIn business configuration error.", type: "error" });
    }
  };

  const handleWordPressConnect = () => {
    if (!isAuthenticated || !idToken) {
      setLoadingPlatform(null);
      setMessage({
        text: "Please log in before connecting WordPress.",
        type: "error",
      });
      return;
    }
    try {
      localStorage.setItem("wordpress_pending_token", idToken);
      initiateWordPressOAuth();
    } catch (error: any) {
      localStorage.removeItem("wordpress_pending_token");
      setLoadingPlatform(null);
      setMessage({
        text: error?.message || "Failed to start WordPress connect",
        type: "error",
      });
    }
  };

  const handleGoogleDriveConnect = () => {
    if (!isAuthenticated || !idToken || !selectedCustomerId) {
      setLoadingPlatform(null);
      setMessage({
        text: "Select a business profile and log in before connecting Google Drive.",
        type: "error",
      });
      return;
    }
    try {
      // No autoOpenPickerOnReturn: connecting from here must not make the Google
      // Picker pop open the next time the user opens chat.
      startGoogleDriveOAuthFlow({
        customerId: selectedCustomerId,
        returnTo: `${location.pathname}${location.search}`,
      });
    } catch (err: any) {
      setLoadingPlatform(null);
      setMessage({
        text: err?.message || "Google Drive configuration error.",
        type: "error",
      });
    }
  };

  const handleGoHighLevelConnect = () => {
    if (!isAuthenticated || !idToken) {
      setLoadingPlatform(null);
      setMessage({
        text: "Please log in before connecting GoHighLevel.",
        type: "error",
      });
      return;
    }
    try {
      // The callback route is public (see App.tsx), so it recovers the session
      // from here rather than from AuthContext, which the redirect tears down.
      localStorage.setItem("ghl_pending_token", idToken);
      initiateGHLOAuth();
    } catch (error: any) {
      localStorage.removeItem("ghl_pending_token");
      setLoadingPlatform(null);
      setMessage({
        text: error?.message || "Failed to start GoHighLevel connect",
        type: "error",
      });
    }
  };

  /**
   * GoHighLevel's disconnect transport, supplied to its card as `onDisconnect`.
   *
   * Intentionally nothing but the call: the confirmation step, the in-flight
   * spinner, the optimistic prune of connectedProfiles and the error surfacing
   * all belong to handleDisconnect and are shared verbatim with every other
   * platform. Only the request differs, because a GHL connection is a sub-account
   * binding rather than a social token.
   *
   * `detail.id` is the GHL location id — _store_ghl_connection stores each
   * connected sub-account with its location id as the profile id.
   */
  const handleGoHighLevelDisconnect = async (detail: ConnectedProfileDetail) => {
    // handleDisconnect already refuses to run without both of these; throwing
    // rather than returning keeps that guarantee explicit, since resolving here
    // would prune the card without anything having been disconnected.
    if (!selectedCustomerId || !idToken) {
      throw new Error("Please log in and select a business profile first.");
    }
    await disconnectGHLLocation(selectedCustomerId, detail.id, idToken);
  };

  /** Shopify's transport, supplied as `onDisconnect`: forgets the store token. Posts already
   *  on the store stay there, so say how many, the same way the Shopify dialog does. */
  const handleShopifyDisconnect = async () => {
    if (!selectedCustomerId || !idToken) {
      throw new Error("Please log in and select a business profile first.");
    }
    const { stillLive } = await disconnectShopify(selectedCustomerId, idToken);
    setShopifyConnected(false);
    if (stillLive > 0) {
      setMessage({
        text: `Shopify disconnected. ${stillLive === 1 ? "1 post is" : `${stillLive} posts are`} still on your store. Remove ${
          stillLive === 1 ? "it" : "them"
        } in Shopify if you no longer want ${stillLive === 1 ? "it" : "them"}.`,
        type: "success",
      });
    }
  };

  /** TikTok's transport, supplied as `onDisconnect`: revokes at TikTok and deletes our token. NOT the generic /tokens route. */
  const handleTikTokDisconnect = async (detail: ConnectedProfileDetail) => {
    if (!selectedCustomerId || !idToken) {
      throw new Error("Please log in and select a business profile first.");
    }
    // Never pair this customer with a profile id loaded for another one.
    if (loadingConnections || profilesCustomerId !== selectedCustomerId) {
      throw new Error("Your workspace just changed — refresh and try again.");
    }
    await disconnectTikTok(selectedCustomerId, detail.id, idToken);
  };

  // A platform is connected if a stored profile matches its platform string and the
  // optional detail-id filters. LinkedIn personal and business both store under
  // "LINKEDIN": the business Page id is a full "urn:li:organization:" URN, while the
  // personal id is the bare member id from /v2/userinfo `sub` (NOT a urn:li:person: URN).
  // So business matches the org prefix; personal matches anything that ISN'T an org.
  // Returns the matching detail (so disconnect has the profile_id/account_id it needs),
  // not just a boolean — when a platform has several connected profiles (e.g. multiple
  // GMB locations) this is the first match, matching the card's existing one-card-per-
  // platform simplification rather than a per-profile list.
  const getConnectedDetail = (platform: {
    platformKey?: string;
    connectedPlatform?: string;
    connectedUrnPrefix?: string;
    connectedUrnExcludePrefix?: string;
  }): ConnectedProfileDetail | undefined => {
    const storedPlatform = platform.connectedPlatform || platform.platformKey;
    if (!storedPlatform) return undefined;
    for (const p of connectedProfiles) {
      if ((p.platform || "").toUpperCase() !== storedPlatform.toUpperCase()) continue;
      if (!Array.isArray(p.details)) continue;
      const match = p.details.find((d) => {
        if (!d.id) return false;
        const id = String(d.id);
        if (platform.connectedUrnPrefix && !id.startsWith(platform.connectedUrnPrefix)) {
          return false;
        }
        if (
          platform.connectedUrnExcludePrefix &&
          id.startsWith(platform.connectedUrnExcludePrefix)
        ) {
          return false;
        }
        return true;
      });
      if (match) return match;
    }
    return undefined;
  };

  const isPlatformConnected = (platform: {
    platformKey?: string;
    connectedPlatform?: string;
    connectedUrnPrefix?: string;
    connectedUrnExcludePrefix?: string;
  }): boolean => getConnectedDetail(platform) !== undefined;

  /**
   * Release one connected profile, whatever platform it belongs to.
   *
   * The transport is the only thing that varies (see `onDisconnect` on
   * PlatformCard); the confirmation state, the in-flight flag, the optimistic
   * prune and the error message are shared by every platform so they all behave
   * identically. Takes the whole PlatformCard rather than the structural subset
   * that getConnectedDetail needs, because the card is what carries the override.
   */
  const handleDisconnect = async (platform: PlatformCard) => {
    const storedPlatform = platform.connectedPlatform || platform.platformKey;
    const detail = getConnectedDetail(platform);
    // A card that tracks its own connection (connectedOverride, e.g. Shopify)
    // has no connected_profiles row; its onDisconnect needs none.
    const ownsConnection = platform.connectedOverride !== undefined && Boolean(platform.onDisconnect);
    if (!storedPlatform || (!detail && !ownsConnection) || !selectedCustomerId || !idToken) return;
    setDisconnectingPlatform(platform.platformKey ?? null);
    setMessage(null);
    try {
      if (platform.onDisconnect) {
        await platform.onDisconnect(
          detail ?? { id: storedPlatform, metadata: { name: platform.name, category: "", type: "" } }
        );
      } else if (detail) {
        await deleteSocialToken(
          selectedCustomerId,
          storedPlatform as Platform,
          detail.id,
          idToken,
          detail.metadata?.account_id
        );
      }
      if (detail) {
        setConnectedProfiles((prev) =>
          prev
            .map((p) =>
              (p.platform || "").toUpperCase() === storedPlatform.toUpperCase()
                ? { ...p, details: p.details.filter((d) => d.id !== detail.id) }
                : p
            )
            .filter((p) => p.details.length > 0)
        );
      }
      setConfirmingDisconnect(null);
      connectionHealth?.refetch();
    } catch (error: any) {
      setMessage({ text: error?.message || "Failed to disconnect", type: "error" });
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  // Arrived here from the app-shell alarm bell's "Reconnect" — start that
  // platform's existing OAuth flow. Latch on the state object identity, not a
  // one-shot boolean: each distinct nav from the bell gets its own object, so a
  // second reconnect on an already-mounted ConnectPage still fires, while a
  // StrictMode double-invoke (same object) does not.
  const handledReconnectStateRef = useRef<unknown>(null);
  useEffect(() => {
    const st = location.state as { reconnectPlatform?: string } | null;
    if (!st?.reconnectPlatform) return;
    if (handledReconnectStateRef.current === location.state) return;
    if (!selectedCustomerId || !idToken) return;
    handledReconnectStateRef.current = location.state;
    const key = String(st.reconnectPlatform).toUpperCase();
    // Clear the nav state but keep ?tab=connect, so a thrown OAuth-URL build
    // leaves the user on the Connect tab rather than the default one.
    navigate(location.pathname + location.search, { replace: true });
    // Adding a platform here? Also add it to RECONNECTABLE_PLATFORMS in
    // useConnectionHealth.ts, or the bell will never surface a row for it.
    if (key === "LINKEDIN") {
      handleLinkedInConnect();
    } else if (key === "LINKEDIN_BUSINESS") {
      handleLinkedInBusinessConnect();
    } else if (key === "FACEBOOK") {
      handleFacebookConnect();
    } else if (key === "INSTAGRAM") {
      handleInstagramConnect();
    } else if (key === "TIKTOK") {
      handleTikTokConnect();
    } else if (key === "BING_WEBMASTER" && bingEnabled) {
      handleBingConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, selectedCustomerId, idToken]);

  // Platform data for cards
  useEffect(() => {
    if (!selectedCustomerId || !idToken) return;
    let active = true;
    setHostedLoading(true);
    getHostedBlog(selectedCustomerId, idToken)
      .then((h) => active && setHostedConnected(h.status === "CONNECTED"))
      .catch(() => active && setHostedConnected(false))
      .finally(() => active && setHostedLoading(false));
    return () => {
      active = false;
    };
  }, [selectedCustomerId, idToken, showHostedBlog]);

  useEffect(() => {
    if (!selectedCustomerId || !idToken) return;
    let active = true;
    setShopifyLoading(true);
    getShopifyConnection(selectedCustomerId, idToken)
      .then((s) => {
        if (!active) return;
        setShopifyShown(s.available || s.connected);
        setShopifyConnected(s.connected && s.status === "CONNECTED");
      })
      .catch(() => active && setShopifyShown(false))
      .finally(() => active && setShopifyLoading(false));
    return () => {
      active = false;
    };
  }, [selectedCustomerId, idToken, showShopify]);

  // Only trust the connected profiles once they are loaded for the CURRENT customer.
  const profilesReady = !loadingConnections && profilesCustomerId === selectedCustomerId;
  const tiktokDetail =
    tiktokEnabled && profilesReady ? getConnectedDetail({ platformKey: "TIKTOK" }) : undefined;
  // Every connected Bing site: "Update key" saves the new key for all of them.
  const bingSiteUrls =
    bingEnabled && profilesReady
      ? connectedProfiles
          .filter((p) => (p.platform || "").toUpperCase() === bingProvider.platformKey)
          .flatMap((p) => (Array.isArray(p.details) ? p.details : []))
          .map((d) => String(d.id || ""))
          .filter(Boolean)
      : [];
  const bingAlert = connectionHealth?.alerts.find(
    (a) => (a.platform || "").toUpperCase() === bingProvider.platformKey
  );
  const platforms: PlatformCard[] = [
    {
      name: "Google My Business",
      platformKey: "GMB",
      logo: "https://lh3.googleusercontent.com/aida-public/AB6AXuBb9jTtISNFdd8SvdvsjN2cZN8rvpsTXnUFtYAnvXGjUT04dEK6WbqlegPyc7vKfr1gne679E_eyERuAn33M-Ca7tS7_TPdlIOF1Mtp6Rv6uTQDKr2aFwIShOsKqgSPBZu5OsimL7WG_wmn795VN9Xynmkl8Cl0SGeUV2F8o5CjCeDmWJdCtqhSZGYhbQ6HRNXyFYKgdKNpcMIonhUz5ongmtrYBzjXmsfYCUK_YDhBaEaGS6BKjTzQ488s6a_WMSs7Le8dOzTeesk",
      description: "Manage your business profile on Google.",
      buttonClass: "bg-blue-600 hover:bg-blue-700",
      onConnect: handleGoogleMyBusinessConnect,
      iconClass: "text-blue-600",
    },
    {
      name: "YouTube",
      platformKey: "YOUTUBE",
      logo: YouTubeLogo,
      description: "",
      buttonClass: "bg-red-600 hover:bg-red-700",
      onConnect: handleYouTubeConnect,
      iconClass: "text-red-600",
      hideTitle: true,
      logoClass: "h-24 w-auto",
    },
    {
      name: "Facebook",
      platformKey: "FACEBOOK",
      logo: "https://lh3.googleusercontent.com/aida-public/AB6AXuCeleKD3zTBZ92cvL0_1KDQmF0lGdNtWUyTItPAqKlunRMabp3decnlLk2hSUuesIqUQ8oTr7YGFLFJb3QaORvOYEVX-Or-oNM_pur5EUjkjftuVJQsOOab9IEn6HmW1p51H0U3bjLP_gMfwE_S8RQoDUkahQo-wCsyOrC9LJ6Sgy_iIMtoQQ4ubQ6CM0eTjUi2LuzyQpyMtHfJD8Wyx_MdE39PNKwWdRMx15LFQzyIzP3HZAOTAzRR-RCqSUkImLsBWq4H19Genxg",
      description: "Integrate with your Facebook pages and groups.",
      buttonClass: "bg-blue-800 hover:bg-blue-900",
      onConnect: handleFacebookConnect,
      iconClass: "text-blue-800",
    },
    {
      name: "Instagram",
      platformKey: "INSTAGRAM",
      logo: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3R5bGU9ImJhY2tncm91bmQ6IGxpbmVhci1ncmFkaWVudCh0byByaWdodCBib3R0b20sICM4YTE1ZmYsICNmZjJlYjcsICNmZjZjMDApOyBib3JkZXItcmFkaXVzOiAxNnB4OyBwYWRkaW5nOiA4cHg7Ij48cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHJ4PSI1IiByeT0iNSI+PC9yZWN0PjxwYXRoIGQ9Ik0xNiAxMS4zN0E0IDQgMCAxIDEgMTIuNjMgOCA0IDQgMCAwIDEgMTYgMTEuMzd6Ij48L3BhdGg+PGxpbmUgeDE9IjE3LjUiIHkxPSI2LjUiIHgyPSIxNy41MSIgeTI9IjYuNSI+PC9saW5lPjwvc3ZnPg==",
      description: "Connect your Instagram account for content management.",
      buttonClass:
        "bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 transition-opacity duration-300",
      onConnect: handleInstagramConnect,
      iconClass: "text-pink-500",
    },
    {
      name: "LinkedIn",
      platformKey: "LINKEDIN",
      connectedUrnExcludePrefix: "urn:li:organization:",
      logo: "/LI-Logo.png",
      description: "",
      buttonClass: "bg-blue-700 hover:bg-blue-800",
      onConnect: handleLinkedInConnect,
      iconClass: "text-blue-700",
      hideTitle: true,
      logoClass: "h-24 w-auto object-contain max-w-full",
    },
    {
      name: "LinkedIn Business Page",
      platformKey: "LINKEDIN_BUSINESS",
      connectedPlatform: "LINKEDIN",
      connectedUrnPrefix: "urn:li:organization:",
      logo: "/LI-Logo.png",
      description: "Connect a LinkedIn Company Page you administer to publish posts.",
      buttonClass: "bg-blue-700 hover:bg-blue-800",
      onConnect: handleLinkedInBusinessConnect,
      iconClass: "text-blue-700",
      logoClass: "h-14 w-auto object-contain max-w-full",
    },
    {
      name: "WordPress",
      platformKey: "WORDPRESS",
      logo: "https://s.w.org/style/images/about/WordPress-logotype-standard.png",
      description:
        "Publish blog posts from Sagelyn to your WordPress.com site.",
      buttonClass:
        "bg-[#0675c4] hover:bg-[#056095] hover:brightness-95 transition-colors",
      onConnect: handleWordPressConnect,
      iconClass: "text-sky-600",
      logoClass: "h-14 w-auto max-w-[200px] mb-4 object-contain",
    },
    ...(shopifyShown
      ? [
          {
            name: "Shopify",
            platformKey: "SHOPIFY",
            logo: SHOPIFY_LOGO,
            description: "Publish blog posts from Sagelyn to your Shopify store's blog.",
            buttonClass: "bg-[#5e8e3e] hover:bg-[#4d7533]",
            // Connect and choose the blog happen in the dialog; Disconnect sits
            // under the card like every other connection.
            onConnect: () => {
              setShowShopify(true);
              setLoadingPlatform(null);
            },
            onDisconnect: handleShopifyDisconnect,
            disconnectNote: "Posts already on your store stay there.",
            connectedAction: {
              label: "CONNECTED",
              icon: "check_circle",
              title: "Connected — click to change which blog your posts go to",
            },
            connectedOverride: shopifyConnected,
            statusLoadingOverride: shopifyLoading,
            iconClass: "text-[#5e8e3e]",
            logoClass: "h-16 w-16 mb-4 object-contain",
          },
        ]
      : []),
    ...(googleDriveEnabled
      ? [
          {
            name: "Google Drive",
            platformKey: "GOOGLE_DRIVE",
            logo: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA4Ny4zIDc4Ij48cGF0aCBkPSJtNi42IDY2Ljg1IDMuODUgNi42NWMuOCAxLjQgMS45NSAyLjUgMy4zIDMuM2wxMy43NS0yMy44aC0yNy41YzAgMS41NS40IDMuMSAxLjIgNC41eiIgZmlsbD0iIzAwNjZkYSIvPjxwYXRoIGQ9Im00My42NSAyNS0xMy43NS0yMy44Yy0xLjM1LjgtMi41IDEuOS0zLjMgMy4zbC0yNS40IDQ0YTkuMDYgOS4wNiAwIDAgMC0xLjIgNC41aDI3LjV6IiBmaWxsPSIjMDBhYzQ3Ii8+PHBhdGggZD0ibTczLjU1IDc2LjhjMS4zNS0uOCAyLjUtMS45IDMuMy0zLjNsMS42LTIuNzUgNy42NS0xMy4yNWMuOC0xLjQgMS4yLTIuOTUgMS4yLTQuNWgtMjcuNTAybDUuODUyIDExLjV6IiBmaWxsPSIjZWE0MzM1Ii8+PHBhdGggZD0ibTQzLjY1IDI1IDEzLjc1LTIzLjhjLTEuMzUtLjgtMi45LTEuMi00LjUtMS4yaC0xOC41Yy0xLjYgMC0zLjE1LjQ1LTQuNSAxLjJ6IiBmaWxsPSIjMDA4MzJkIi8+PHBhdGggZD0ibTU5LjggNTNoLTMyLjNsLTEzLjc1IDIzLjhjMS4zNS44IDIuOSAxLjIgNC41IDEuMmg1MC44YzEuNiAwIDMuMTUtLjQ1IDQuNS0xLjJ6IiBmaWxsPSIjMjY4NGZjIi8+PHBhdGggZD0ibTczLjQgMjYuNS0xMi43LTIyYy0uOC0xLjQtMS45NS0yLjUtMy4zLTMuM2wtMTMuNzUgMjMuOCAxNi4xNSAyOGgyNy40NWMwLTEuNTUtLjQtMy4xLTEuMi00LjV6IiBmaWxsPSIjZmZiYTAwIi8+PC9zdmc+Cg==",
            description: "Attach files from your Google Drive in chat.",
            buttonClass: "bg-[#1a73e8] hover:bg-[#1666cc]",
            onConnect: handleGoogleDriveConnect,
            iconClass: "text-blue-600",
            logoClass: "h-16 w-16 mb-4 object-contain",
            connectedOverride: googleDriveConnected,
            statusLoadingOverride: googleDriveStatusLoading,
          },
        ]
      : []),
    ...(gscEnabled
      ? [
          {
            name: "Google Search Console",
            platformKey: "GOOGLE_SEARCH_CONSOLE",
            logo: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0OCA0OCI+PHBhdGggZmlsbD0iIzQyODVmNCIgZD0iTTYgMzZoMzZ2NEg2eiIvPjxwYXRoIGZpbGw9IiMzNGE4NTMiIGQ9Ik0xMCAyMmg2djEyaC02eiIvPjxwYXRoIGZpbGw9IiNmYmJjMDQiIGQ9Ik0yMSAxNGg2djIwaC02eiIvPjxwYXRoIGZpbGw9IiNlYTQzMzUiIGQ9Ik0zMiAyNmg2djhoLTZ6Ii8+PC9zdmc+",
            description:
              "Track how your site performs in Google Search.",
            buttonClass: "bg-[#1a73e8] hover:bg-[#1666cc]",
            onConnect: handleSearchConsoleConnect,
            iconClass: "text-blue-600",
            logoClass: "h-16 w-16 mb-4 object-contain",
          },
        ]
      : []),
    ...(bingEnabled
      ? [
          {
            name: bingProvider.label,
            platformKey: bingProvider.platformKey,
            ...bingProvider.card,
            onConnect: handleBingConnect,
            logoClass: "h-16 w-16 mb-4 object-contain",
            // A row from the OAuth build, or one the backend marked dead: a new key fixes it.
            needsReconnect: bingAlert?.level === "red",
            connectedAction: {
              label: "Update key",
              icon: "key",
              title: "Connected with your Bing API key — click to paste a new one",
            },
            disconnectNote: "You can also delete the key in Bing Webmaster Tools.",
            cardExtra: bingSiteUrls.length ? (
              <div className="mb-5 -mt-2 text-sm">
                <p className="font-medium text-gray-700">
                  {bingAlert?.level === "red" ? "Connect again with your API key" : "Connected (API key)"}
                </p>
                <p className="mt-0.5 break-all text-gray-500">{bingSiteUrls.join(", ")}</p>
                {bingAlert?.reason === "rotate_key" && (
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                    <span className="material-icons text-[14px]" aria-hidden>
                      schedule
                    </span>
                    Time to refresh your Bing key
                  </p>
                )}
              </div>
            ) : undefined,
          },
        ]
      : []),
    ...(tiktokEnabled
      ? [
          {
            name: "TikTok",
            platformKey: "TIKTOK",
            logo: TIKTOK_LOGO,
            description: "Publish your videos to TikTok.",
            buttonClass: "bg-black hover:bg-gray-800",
            onConnect: handleTikTokConnect,
            // Not in DISCONNECTABLE_PLATFORM_KEYS: the generic /tokens delete would 500 for TikTok.
            onDisconnect: handleTikTokDisconnect,
            iconClass: "text-gray-900",
            logoClass: "h-16 w-16 mb-4 object-contain",
            needsReconnect: tiktokNeedsReconnect,
            cardExtra:
              tiktokDetail && selectedCustomerId && idToken ? (
                <TikTokCardStatus
                  customerId={selectedCustomerId}
                  idToken={idToken}
                  profileId={tiktokDetail.id}
                  displayName={tiktokDetail.metadata?.display_name}
                  onReconnectNeeded={setTiktokNeedsReconnect}
                />
              ) : undefined,
          },
        ]
      : []),
    {
      name: "GoHighLevel",
      platformKey: "GHL",
      logo: "https://lh3.googleusercontent.com/aida-public/AB6AXuCvxOUaMbU-UcTPJDxPj-TBz-YeOIg9lYhpQTJoASYx547xPeco0J8XR9JfVyggRsgXMsy2_MmwrVatEHqxcjD-8RxdvJokhXt0BmwJbORlNWTOpwiP6jL_rV3YOwdB5z55muL1kJeLWWavWgQumBOUrHOGorjX35ea6Ck_T2YeqDdPmC9rzfrj_IEhKhl01lg1Gk_Yyt4WhN0AZQc8pDxcYbcl6U5ah4NEMw-0HBCfzfBEtY4zGKXrgYVhBr_bAYqAwL7oVpifThM",
      description: "Sync your customer relationship management data.",
      buttonClass: "bg-sky-600 hover:bg-sky-700",
      onConnect: handleGoHighLevelConnect,
      // Not in DISCONNECTABLE_PLATFORM_KEYS: a GHL connection is released through
      // its own endpoint, not the shared token route. An agency install can link
      // several sub-accounts at once, and getConnectedDetail returns the first —
      // so this disconnects one location per click, matching how the page already
      // handles multi-location platforms such as GMB.
      onDisconnect: handleGoHighLevelDisconnect,
      iconClass: "text-sky-500",
    },
    {
      name: "Squarespace, Wix & other site builders",
      platformKey: "HOSTED_BLOG",
      logo: HOSTED_BLOG_LOGO,
      description: "No blog API? We host your blog on your own domain, like blog.yoursite.com. One setting.",
      buttonClass: "bg-brand-primary hover:bg-brand-primary-hover",
      // Opens the connect flow (and manage/disconnect once connected) in a dialog.
      onConnect: () => {
        setShowHostedBlog(true);
        setLoadingPlatform(null);
      },
      connectedOverride: hostedConnected,
      statusLoadingOverride: hostedLoading,
      iconClass: "text-brand-primary",
      logoClass: "h-16 w-16 mb-4 object-contain",
    },
  ];
  const sectionsWithCards = SECTIONS.map((sec) => ({
    ...sec,
    cards: platforms.filter((p) => (SECTION_BY_KEY[p.platformKey ?? ""] ?? "other") === sec.key),
  })).filter((sec) => sec.cards.length > 0);
  const isCardConnected = (p: PlatformCard) => Boolean(p.connectedOverride ?? isPlatformConnected(p));
  // One section at a time, as underlined tabs. ?section=<key> links straight to
  // one (e.g. the blog editor's "connect another website").
  const requestedSection = new URLSearchParams(location.search).get("section") ?? "";
  const activeSection =
    sectionsWithCards.find((s) => s.key === requestedSection)?.key ?? sectionsWithCards[0]?.key ?? "";
  const selectSection = (key: string) => {
    const params = new URLSearchParams(location.search);
    params.set("section", key);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const outerClass = embedded
    ? "w-full"
    : "min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8";
  const headerClass = embedded ? "mb-8 text-center" : "mb-12 text-center";

  return (
    <div className={outerClass}>
      <div className="mx-auto w-full max-w-4xl">
        <header className={headerClass}>
          <h1
            className={
              embedded
                ? "text-2xl font-bold text-gray-800"
                : "text-4xl font-bold text-gray-800"
            }
          >
            Connect Your Platforms
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Connect the places your agents work: social media, your website,
            search, your CRM and your files.
          </p>
        </header>

        {message && (
          <div
            className={`mb-4 p-4 rounded ${
              message.type === "success"
                ? "bg-theme-primary/10 text-theme-primary"
                : "bg-red-100 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}
        <main>
          <div role="tablist" aria-label="Connection types" className="mb-6 flex overflow-x-auto border-b border-gray-200">
            {sectionsWithCards.map((sec) => {
              const selected = sec.key === activeSection;
              const connected = sec.cards.filter(isCardConnected).length;
              return (
                <button
                  key={sec.key}
                  type="button"
                  role="tab"
                  id={`connect-tab-${sec.key}`}
                  aria-selected={selected}
                  aria-controls={`connect-sec-${sec.key}`}
                  onClick={() => selectSection(sec.key)}
                  className={`flex min-h-[48px] min-w-[150px] flex-1 items-center justify-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-semibold transition-colors ${
                    selected
                      ? "border-brand-primary text-gray-900"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  {sec.title}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      connected ? "bg-brand-primary/10 text-brand-primary" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {connected}/{sec.cards.length}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Every panel stays rendered (instant switching); only the selected one shows. */}
          {sectionsWithCards.map((sec) => (
          <section
            key={sec.key}
            id={`connect-sec-${sec.key}`}
            role="tabpanel"
            aria-labelledby={`connect-tab-${sec.key}`}
            hidden={sec.key !== activeSection}
          >
            <p className="mb-5 text-left text-sm text-gray-500">{sec.why}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {sec.cards.map((platform) => {
              const isConnected =
                platform.connectedOverride ?? isPlatformConnected(platform);
              return (
              <div
                key={platform.name}
                data-testid={`platform-card-${platform.platformKey ?? platform.name}`}
                className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col items-center text-center"
              >
                <div className="flex-1 flex flex-col items-center">
                  <img
                    alt={`${platform.name} logo`}
                    className={
                      platform.logoClass || "h-16 w-16 mb-4 object-contain"
                    }
                    src={platform.logo}
                    style={
                      platform.logoClass
                        ? { objectFit: "contain", maxWidth: "100%" }
                        : undefined
                    }
                  />
                  {!platform.hideTitle && (
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {platform.name}
                    </h3>
                  )}
                  {platform.description && (
                    <p className="text-gray-500 mb-6">{platform.description}</p>
                  )}
                  {platform.cardExtra}
                </div>
                {(() => {
                  const isConnecting = loadingPlatform === platform.platformKey;
                  // Coming-soon cards keep their own label; everything else shows
                  // a loading state until the connection status is known.
                  // ?? not ||: `false` is a meaningful override and must not fall
                  // through to the page-level connections spinner.
                  const isStatusLoading =
                    (platform.statusLoadingOverride ?? loadingConnections) &&
                    !platform.isComingSoon;
                  const busy = isConnecting || isStatusLoading;
                  return (
                    <button
                      type="button"
                      className={`w-full text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center mt-auto ${
                        isConnected
                          ? "bg-brand-primary hover:bg-brand-primary-hover"
                          : platform.buttonClass
                      } ${busy ? "opacity-75 cursor-wait" : ""}`}
                      title={
                        isConnected
                          ? platform.connectedAction?.title ?? "Connected — click to change site"
                          : undefined
                      }
                      onClick={() => {
                        if (
                          !platform.isComingSoon &&
                          !loadingPlatform &&
                          !loadingConnections
                        ) {
                          setLoadingPlatform(platform.platformKey ?? null);
                          void Promise.resolve(platform.onConnect()).catch(() => {
                            /* errors handled inside handlers via setMessage */
                          });
                        }
                      }}
                      disabled={
                        platform.isComingSoon ||
                        !!loadingPlatform ||
                        isStatusLoading
                      }
                    >
                      {isConnecting ? (
                        <>
                          <ButtonSpinner />
                          Connecting…
                        </>
                      ) : isStatusLoading ? (
                        <>
                          <ButtonSpinner />
                          Loading…
                        </>
                      ) : (
                        <>
                          <span className="material-icons mr-2">
                            {isConnected
                              ? platform.connectedAction?.icon ?? "check_circle"
                              : "add_circle_outline"}
                          </span>
                          {platform.isComingSoon
                            ? "Coming Soon"
                            : isConnected && platform.needsReconnect
                            ? "Reconnect"
                            : isConnected
                            ? platform.connectedAction?.label ?? "CONNECTED"
                            : "Connect"}
                        </>
                      )}
                    </button>
                  );
                })()}
                {isConnected &&
                  !platform.isComingSoon &&
                  canDisconnect(platform) &&
                  (confirmingDisconnect === platform.platformKey ? (
                    // Confirm step: the destructive action is the one that has to
                    // be deliberate, so it gets the solid red fill and Cancel stays
                    // quiet. Both sized to match the Connect button's rhythm.
                    <>
                    {platform.disconnectNote && (
                      <p className="mt-3 text-xs text-gray-500">{platform.disconnectNote}</p>
                    )}
                    <div className="mt-2 grid w-full grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleDisconnect(platform)}
                        disabled={disconnectingPlatform === platform.platformKey}
                        className="flex items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-red-700 disabled:cursor-wait disabled:opacity-60"
                      >
                        {disconnectingPlatform === platform.platformKey ? (
                          <>
                            <ButtonSpinner />
                            Disconnecting…
                          </>
                        ) : (
                          "Yes, disconnect"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDisconnect(null)}
                        disabled={disconnectingPlatform === platform.platformKey}
                        className="rounded-lg border border-white/20 px-3 py-2 text-xs font-semibold text-gray-300 transition-colors duration-200 hover:bg-white/10 hover:text-white disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    </>
                  ) : (
                    // Full-width and bordered so it reads as a real control rather
                    // than stray text, but outline-only and muted so it never
                    // competes with the primary Connect action above it.
                    <button
                      type="button"
                      onClick={() => setConfirmingDisconnect(platform.platformKey ?? null)}
                      title="Disconnect this account — you will need to reconnect through the platform to publish again"
                      className="mt-2 flex w-full items-center justify-center rounded-lg border border-white/15 py-2 px-4 text-xs font-semibold text-gray-400 transition-colors duration-200 hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-400"
                    >
                      <span className="material-icons mr-1.5 text-sm leading-none">
                        link_off
                      </span>
                      Disconnect
                    </button>
                  ))}
              </div>
              );
            })}
          </div>
          </section>
          ))}
        </main>
        {showHostedBlog && selectedCustomerId && idToken && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Connect your website's blog"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8"
            onClick={() => setShowHostedBlog(false)}
          >
            <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setShowHostedBlog(false)}
                aria-label="Close"
                className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <span className="material-icons">close</span>
              </button>
              <WebsiteBlogSection customerId={selectedCustomerId} idToken={idToken} inDialog />
            </div>
          </div>
        )}
        {showShopify && selectedCustomerId && idToken && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Connect your Shopify store"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8"
            onClick={() => setShowShopify(false)}
          >
            <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setShowShopify(false)}
                aria-label="Close"
                className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <span className="material-icons">close</span>
              </button>
              <ShopifyBlogSection customerId={selectedCustomerId} idToken={idToken} inDialog />
            </div>
          </div>
        )}
        {/* Waits for the connections: opened from the bell before they load, it
            would otherwise start as a new connect rather than "Update key". */}
        {bingDialogCustomerId && bingDialogCustomerId === selectedCustomerId && idToken && profilesReady && (
          <BingKeyDialog
            customerId={bingDialogCustomerId}
            idToken={idToken}
            connectedSiteUrls={bingSiteUrls}
            onClose={handleBingDialogClose}
          />
        )}
      </div>

    </div>
  );
};

export default ConnectPage;